import { useCallback, useEffect, useRef, useState } from 'react'

import { CarNode, useAllCarsQuery } from '@/apollo/graphql'
import { getFetchMoreStatus } from '@/utils'
import { IPageInfo } from '@/types'

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = (customerId?: number) => {
  const prevKeyword = useRef<string>('')
  const [search, setSearch] = useState<string>('')
  const [cars, setCars] = useState<Partial<CarNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading, networkStatus } = useAllCarsQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      text: search,
      customer: customerId,
    },
    onCompleted: res => {
      if (res.allCars?.edges) {
        const _cars: Partial<CarNode>[] = []
        res.allCars.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) _cars.push(e.node)
        })

        setCars(prev => [...prev, ..._cars])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allCars?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: cars.length }))
  }, [cars?.length])

  const onSearch = useCallback((text: string) => {
    if (prevKeyword.current !== text) {
      setCars([])
      prevKeyword.current = text
    }
    setSearch(text)
    setPageInfo(initialPageInfo)
  }, [])

  return {
    loading,
    onSearch,
    pageInfo,
    data: cars,
    onFetchMore,
  }
}
